import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { patchMe } from "@/lib/auth/auth-api";
import { useIsAuthenticated } from "@/lib/auth/auth-context";
import { SUPPORTED_LANGUAGES, type Language } from "@/lib/i18n/i18n";

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const isAuthenticated = useIsAuthenticated();
  const current = (i18n.resolvedLanguage ?? i18n.language) as string;

  const choose = (lng: Language) => {
    void i18n.changeLanguage(lng);
    // Remembered on the account for mail sent outside this browser's requests
    // (Stripe webhook, scheduled reminders). The interface has already
    // switched; a failed save only leaves that mail in its previous language.
    if (isAuthenticated) void patchMe({ language: lng }).catch(() => {});
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t("language.label")}>
          <Globe aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("language.label")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LANGUAGES.map((lng: Language) => (
          <DropdownMenuCheckboxItem
            key={lng}
            checked={current.startsWith(lng)}
            onCheckedChange={() => choose(lng)}
          >
            {t(`language.${lng}`)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
